<template>
	<div style="height: 100%;">
		<div v-if="list_type && contents" style="height: 100%;">
      <k-panel fill-height :body-scrollable="false" addable searchable @add="open_add_data" @search="search = $event">

        <!-- Title -->
        <template v-slot:title>
          {{content_list.name}}
        </template>

        <!-- Buttons -->
        <template v-slot:left-buttons>
          <k-btn size="m" no-margin icon
            :active="is_pinned"  
            @click="toggle_pin(content_list.id)" 
            v-if="!$compact_view() && 'enable-pin'"
            icon-name="push_pin"
          />

          <k-btn size="m" no-margin icon
            @click="open_edit"
            v-if="!$compact_view() && 
              $is_privilege(['root', 'developer', 'admin', 'group_admin', 'device_admin']) &&
              'enable-edit'"
            icon-name="edit"
          />

          <k-btn size="m"
            v-if="$is_admin() && !$compact_view() && !disableImport" 
            @click.stop="open_import()" 
          >Import</k-btn>

          <k-btn size="m" 
            v-if="is_import_json" 
            @click.stop="open_json_import()"
          >Import JSON</k-btn>

          <k-btn size="m"
            v-if="$is_admin() && !$compact_view() && !disableImport" 
            @click.stop="open_multi_upload()" 
          >Upload files</k-btn>
        </template>

        <template v-slot:default>
          <!-- Breadcrumbs -->
          <k-breadcrumbs v-if="includePath" :items="list_path" @open-link="open_list"/>

          <!-- Table -->
          <k-data-table 
            v-model="selected" 
            :headers="headers" 
            :items="contents" 
            :search="search" 
            fill-height
            :pagination.sync="pagination" 
            :rows-per-page-items="[5, 15, 25, 50, 100, { text: 'All', value: -1 }]"
          >
            <!-- Table headers -->
            <template v-slot:headers="props">
              <tr>
                <th v-if="$is_privilege(['root', 'developer', 'admin', 'group_admin', 'device_admin'])">
                  <k-checkbox
                    :input-value="props.all"
                    :indeterminate="props.indeterminate"
                    primary
                    hide-details
                    @change="toggleAll"
                  ></k-checkbox>
                </th>
                <th
                  :class="[
                    'text-'+header.align, 
                    'column', 
                    header.sortable ? 'sortable' : '', 
                    pagination.descending ? 'desc' : 'asc', 
                    header.value === pagination.sortBy ? 'active' : ''
                  ]"
                  v-for="header in props.headers"
                  :key="header.text"
                  @click="changeSort(header.value)"
                >
                <!-- Header buttons -->
                <template v-if="header.value == 'actions' && $is_privilege(['root', 'developer', 'admin', 'group_admin', 'device_admin'])">
                  <k-btn icon size="s"
                    @click.stop="changeSelectedItemsState('active')" 
                    :disabled="selected.length == 0 || list_type.fields['disable_hide/show']"
                  >
                    <k-badge overlap v-show="!list_type.fields['disable_hide/show']">
                      <template v-slot:badge>
                        <span v-if="selected.length > 0">{{selected.length}}</span>
                      </template>
                      <k-icon size="s">visibility</k-icon>
                    </k-badge>
                  </k-btn>

                  <k-btn icon size="s"
                    @click.stop="changeSelectedItemsState('archived')" 
                    :disabled="selected.length == 0"
                    v-show="!list_type.fields['disable_hide/show']"
                  >
                    <k-badge overlap>
                      <template v-slot:badge>
                        <span v-if="selected.length > 0">{{selected.length}}</span>
                      </template>
                      <k-icon size="s">visibility_off</k-icon>
                    </k-badge>
                  </k-btn>

                  <k-btn icon color="danger" size="s"
                    @click.stop="delete_selected_items()" 
                    :disabled="selected.length == 0"
                  >
                    <k-badge class="overlap2" overlap>
                      <template v-slot:badge>
                        <span v-if="selected.length > 0">{{selected.length}}</span>
                      </template>
                      <k-icon size="s">delete</k-icon>
                    </k-badge>
                  </k-btn>
                </template>
                <template v-else>
                  {{ header.text }}
                  <k-icon small>arrow_upward</k-icon>
                </template>
                </th>
              </tr>
            </template>

            <!-- Table -->
            <template slot="items" slot-scope="props">
              <tr :active="props.selected" @click="props.select(!props.selected)">
              <!-- Table checkbox -->
              <td v-if="$is_privilege(['root', 'developer', 'admin', 'group_admin', 'device_admin'])">
                <k-checkbox
                  :value="props.selected"
                  color="primary"
                  hide-details
                ></k-checkbox>
              </td>

              <!-- Table fields  -->
              <td @click.stop="open_data(props.item.id)">{{props.item.id}}</td>
              <template v-for="field in fields">

                <td v-if="field.type != 'file' && field.type != 's3file' && field.type != 'multiline' && is_access(field, props.item)" :key="field.name" class="inplace-cell">
                  <k-edit-dialog lazy @save="save_inplace(props.item, field)">
                    <span v-html="get_data(props.item.data, field)"></span>
                    <template v-slot:input>
                      <component v-bind:is="get_field_type(field.type)" v-bind="field" v-model="props.item.data" :ref="field.name"></component>
<!--                          <k-btn flat left color="green" @click.stop="save_inplace(props.item, field)">Save</k-btn>-->
                    </template>
                  </k-edit-dialog>
                </td>

                <td v-else @click.stop="open_data(props.item.id)">
                  <span v-html="get_data(props.item.data, field)"></span>
                </td>

              </template>

              <!-- Table buttons -->
              <td style="text-align: right;">
                <k-btn icon size="s" no-margin
                  v-show="props.item.state != 'archived' && !list_type.fields['disable_hide/show']" 
                  @click.stop="changeItemState(props.item, 'archived')"
                  icon-name="visibility"
                />

                <k-btn icon size="s" no-margin
                  v-show="props.item.state == 'archived'" 
                  @click.stop="changeItemState(props.item, 'active')"
                  icon-name="visibility_off"
                />

                <k-btn icon size="s" no-margin
                  @click.stop="open_data(props.item.id)"
                  icon-name="edit"
                />

                <k-btn color="danger" no-margin icon  size="s"
                  @click.stop="delete_item(props.item)" 
                  v-if="$is_privilege(['root', 'developer', 'admin', 'group_admin', 'device_admin'])"
                  icon-name="delete"
                />
              </td>
              </tr>
            </template>
      </k-data-table>

        </template>
      </k-panel>

			
    </div>

    <BottomSheet v-if="$compact_view()"></BottomSheet>

	</div>
</template>

<script>
import Vue from 'vue'
import ContentModel from '@/models/content.js'
import datelib from '@/lib/date.js'
import BottomSheet from '../BottomSheet'

import * as FormItems from '@/components/form_items'

export default {
	props: {
    id: String,
    list: String,
//    search: String,
    editpath: String,
    'disable-import': { type: Boolean, default: false },
    'enable-pin': { type: Boolean, default: false },
    'enable-edit': { type: Boolean, default: false },
    'include-path': { type: Boolean, default: false }
  },
	data () {
		return {
			channel: undefined,
			list_data: {},
      pagination: {
        sortBy: 'identifier',
        descending: false,
        rowsPerPage: 25
      },
      selected: [],
      search: "",
		}
	},
  components: {
    ...FormItems,
    BottomSheet
  },
  watch: {
    "id": function(newValue) {
      this.reconnect();
    },
    "list": function(newValue) {
      this.reconnect();
    }
  },
	computed: {
		headers () {
			var ret = [{ text: 'Id', value: 'id', align: 'left' }];
			_.each(this.fields, (field) => {
				ret.push({ text: (field.label || field.title), value: 'data.' + field.name, align: 'left', sortable: true});
			});
			ret.push({ text: 'Actions', value: 'actions', align: 'right', sortable: false});
			return ret;
		},
		content_list () {
      return _.find(this.content_lists, (cl) => {
        return cl.id == this.get_list_id();
      });
    },
    fields () {
      var lt = this.list_type;
      if (typeof(lt.fields["list-fields"]) != "undefined") {
        return _.filter(lt.fields["fields"], function(field) {
          return lt.fields["list-fields"].includes(field["name"]);
        })

      }

      return lt.fields["fields"];
    },
    list_type () {
      var cl = this.content_list;
      if (typeof(cl) != "undefined" && cl != null) {
        return _.find(this.list_types, (lt) => {
          return lt.id == cl.cms_list_type_id;
        });
      }
    },
    is_import_json () {
      if (this.$is_privilege('developer')) return true;
      if (typeof(this.list_type.fields) != "undefined") {
        return this.list_type.fields.import_json || false;
      }
      return false;
    },
    content_lists () {
      return Object.values(this.$store.state.cms_lists);
    },
    list_types () {
      return Object.values(this.$store.state.cms_list_types);
    },
		contents () {
			return Object.values(this.list_data);
		},
    is_pinned() {
      let lists = _.get(this.$store.state.account, 'meta.cms_pinned_lists', []);
      if(lists.indexOf(this.content_list.id) > -1) return true;
      return false;
    },
    list_path () {
      let path = ContentModel.get_path(this.list, {order: 'parent-child'});
      path = path.map((id) => {
        return { text: this.cms_lists[id].name, href: id}
      })
      return path;
    },
    cms_lists () {
      return this.$store.state.cms_lists;
    }
	},
	mounted() {
		this.connect_ws();
    this.$store.dispatch("get_profile");
	},
 	beforeDestroy: function() {
    this.channel.leave();
  },
	methods: {
    open_list (id) {
      this.$router.push("/content/list/"+id);
    },
    open_edit () {
      this.$router.push("/content/list/"+this.list+"/edit");
    },
    get_list_id() {
      return this.list || this.id;
    },
    toggleAll (newValue) {
      if (newValue) {
        this.selected = this.contents.slice();
      } else {
        this.selected = [];
      }
    },
    toggle_pin(id) {
      if (!this.$store.state.account) return;
      let acc = _.cloneDeep(this.$store.state.account);
      acc.meta.cms_pinned_lists = _.xor(acc.meta.cms_pinned_lists, [id]);
      this.$store.dispatch("update_profile", acc);
    },
    changeSort (column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending
      } else {
        this.pagination.sortBy = column
        this.pagination.descending = false
      }
    },
    changeItemState (item, state) {
      item.state = state;
      this.save_inplace(item, "state");
    },
    changeSelectedItemsState(state) {
      var promises = _.map(this.selected, (item) => {
        return new Promise((resolve, reject) => {
          item.state = state;
          this.save_inplace(item, "state", true, resolve, reject);
        });
      });
      Promise.allSettled(promises).then((results) => {
        if(_.countBy(results, (r) => { return r.status })["fulfilled"] == promises.length) {
          this.$message.success("List data was updated!");
        } else {
          this.$message.warning("Unable to update list data!");
        }
      });
    },
    save_inplace (item, field, hide_messages, success, failed) {
      var self = this;

      if (typeof(this.channel) != "undefined") {
        // Prepare save data for send
        let send_list_data = _.cloneDeep(item);
        send_list_data.cms_list_id = self.get_list_id();

        this.channel
          .push("save", {list_data: send_list_data})
          .receive("ok", (msg) => {
              if(!hide_messages) self.$message.success("List data was updated!");
              if(_.isFunction(success)) success(msg);
          })
          .receive("error", (msg) => {
            console.log(msg);
            if(!hide_messages) self.$message.warning("Unable to update list data!");
            if(_.isFunction(failed)) failed(msg);
          });
      }
    },

    is_access(field, content_item) {
      return ContentModel.is_access(field, content_item.id, this.list);
    },

		get_data (item, field) {
			var data = item[field.name];
			if (field.type == "list_select") {
				var cl = this.$store.state.cms_lists[data];
				if (typeof(cl) != "undefined") return cl.name;
				return data;
			}

      if (field.type == "file") {
        if (typeof data === 'undefined' || data === "") { return "" }

        // Migrate 's3file' type field data for display
        let url = data
        if (typeof data === 'Object' || data instanceof Object) {
          url = _.get(data, "url", "")
        }

        if (field["content-type"] == "image") return "<img style='height: 20px;' src='" + url + "'>"
      }

      // Get S3 file field values from JSON
      if (field.type == "s3file") {
        if (typeof data === 'undefined' || data === "") { return "" }

        let dataString = ""

        // Migrate 'file' type field data for display
        let url = _.get(data, "url", "")
        let statusCode = _.get(data, "status", "")
        let statusMessage = _.get(data, "statusMessage", "")

        if (typeof data === 'string' || data instanceof String) {
          url = data
          statusCode = "ready"
          statusMessage = ""
        }

        if (field["content-type"] == "image") {
          dataString = '<img style="height: 20px;" src="' + url + '"> '
        }

        dataString += '<span title="' + statusMessage + '">' + statusCode + '</span>'

        return dataString
      }

      if (field.type == "multiline") {
        return "Multiline"
      }
      if (field.type == "datetime") {
        return datelib.format_date(data) +" - "+datelib.format_time(data);
      }
      if (field.type == "date") {
        return datelib.format_date(data);
      }
      if (field.type == "content_select") {
        if (typeof(data) != "undefined") {
          if (typeof(data) == "string") return data;
          return data["name"] || "";
        } else {
          return "";
        }
      }
      if (field.type == "playoff_tree") {
        return "-";
      }
      if (field.type == "checkbox") {
        if(data) {
          return field["true-text"] || '<i aria-hidden="true" class="k-icon fa fa-'+(field["true-icon"]||'check')+' theme--dark"></i>';
        } else {
          return field["false-text"] || '<i aria-hidden="true" class="k-icon fa fa-'+(field["false-icon"])+' theme--dark"></i>';
        }
      }
      if(field.type == "text" && _.startsWith(data, "http")) {
        return "<a class='light-blue--text text--lighten-4' href='"+data+"' target='_blank' onclick='return false;' ondblclick='window.location = this.href;'>"+data+"</a>";
      }
			return data;
		},
    get_field_type(field_type) {
      return ContentModel.get_field_type(field_type);
    },
    edit_path_prefix () {
      if (typeof(this.editpath) == "undefined") return "/content/list/";
      return this.editpath;
    },

		open_data (id) {
			this.$router.push(this.edit_path_prefix() + this.get_list_id() + "/data/edit/"+id);
		},
    open_add_data () {
      this.$router.push(this.edit_path_prefix() + this.get_list_id()+"/data/new");
    },
    open_import () {
      this.$router.push(this.edit_path_prefix() + this.get_list_id() + "/data/import");
    },
    open_multi_upload () {
      this.$router.push(this.edit_path_prefix() + this.get_list_id() + "/data/multi_upload");
    },
    open_json_import () {
      this.$router.push(this.edit_path_prefix() + this.get_list_id() + "/data/json_import");
    },
		delete_item (item, hide_messages, success, failed) {
			var self = this;
			this.channel.push("delete", {list_data: item})
      	.receive("ok", (msg) => {
      		if(!hide_messages) self.$message.success("List data deleted!");
          Vue.delete(self.list_data, msg.id);
          if(_.isFunction(success)) success(msg);
        })
        .receive("error", (msg) => {
        	console.log(msg);
        	if(!hide_messages) self.$message.warning("Cannot remove list data");
          if(_.isFunction(failed)) failed(msg);
        });
		},
    delete_selected_items(hide_messages, success, failed) {
      var self = this;
      this.channel.push("delete", {list_data_ids: _.map(this.selected, (item) => { return item.id; })})
        .receive("ok", (msg) => {
          if(!hide_messages) self.$message.success("Selected list items deleted!");
          _.each(msg.list_datas, (deleted_item) => {
            Vue.delete(self.list_data, deleted_item.id);
          })
          if(_.isFunction(success)) success(msg);
        })
        .receive("error", (msg) => {
          console.log(msg);
          if(!hide_messages) self.$message.warning("CCannot remove selected list items!");
          if(_.isFunction(failed)) failed(msg);
        });

    },
    reconnect() {
      this.channel.off("init");
      this.channel.off("update");
      this.channel.off("delete");
      this.channel.leave();
      this.list_data = {};
      this.selected = [];
      this.connect_ws();
    },
		connect_ws () {
      var self = this;
      this.channel = this.$cloudserver.channel("cms:list_datas:"+this.get_list_id());
      this.channel.on("init", function(msg) {
      	msg.list_data.forEach(function(d) {
      		Vue.set(self.list_data, d.id, d);
      	});
      });
      this.channel.on("update", function(msg) {
        console.log("List data update", msg);
      	Vue.set(self.list_data, msg.list_data.id, msg.list_data);
      });
      this.channel.on("delete", function(msg) {
        console.log("List data delete", msg);
				Vue.delete(self.list_data, msg.list_data.id);
      });
      this.channel.join();
    }
	}
}
</script>

<style>
.ks-data-table-table tbody td.inplace-cell {
  padding: 0;
  height: 1px;
}

.inplace-cell > .ks-menu,
.inplace-cell > .ks-menu > .ks-menu-activator,
.inplace-cell .ks-edit-dialog-activator {
  display: flex;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

.inplace-cell .ks-edit-dialog-activator {
  padding: 8px 16px;
  min-height: 32px;
}
</style>
